// Typed reads of the admin surfaces the cockpit is built on. Core's own
// admin routes (admin.go) are typed in ./schema; the three proxied
// services are not, so their shapes are declared here from the Go structs
// they come from -- with one deliberate difference from JsonTable's
// dump-everything approach: the fleet type below is *narrow*, and it is
// narrow on purpose.
import {
  adminListTasks,
  type AdminSummary,
  proxyCompute,
  proxyKillswitch,
  proxyPolicy,
  type TaskView,
} from "./client";

// ---- KillSwitch ---------------------------------------------------------

export interface KillSwitchInputs {
  budget_total_minor?: number;
  billed_total_minor?: number;
  idle_earned_total_minor?: number;
  runpod_spent_total_minor?: number;
  vast_spent_total_minor?: number;
  last_billed_at?: string | null;
  last_runpod_at?: string | null;
  last_vast_at?: string | null;
}

export interface KillSwitchState {
  tripped: boolean;
  tripped_at?: string | null;
  reason?: string | null;
  /** budget + billed - spend (internal/killswitch/admin.go:27-71): the
   * number that answers "how much can still burn". */
  balance_minor?: number;
  inputs?: KillSwitchInputs;
  /** RunPod pods with our name plus RUNNING serverless workers, counted by
   * KillSwitch from RunPod itself; null until its first poll. */
  live_resources?: { runpod?: number | null; vast?: number | null };
  /** The provider's own account readout from KillSwitch's last successful
   * poll: absent before the first, frozen while tripped. */
  providers?: {
    runpod?: { balance_minor: number; spend_per_hr_minor: number; read_at: string };
    /** Present only when Vast is wired (VAST_API_URL). */
    vast?: { balance_minor: number; read_at: string };
  };
}

export interface KillSwitchEvent {
  id: number;
  at: string;
  kind: string;
  details: Record<string, unknown>;
}

export function fetchKillSwitchEvents(limit = 20): Promise<KillSwitchEvent[]> {
  return proxyKillswitch<{ events: KillSwitchEvent[] }>(`events?limit=${limit}`).then((r) => r.events ?? []);
}

export function fetchKillSwitchState(): Promise<KillSwitchState> {
  return proxyKillswitch<KillSwitchState>("state");
}

// ---- Policy -------------------------------------------------------------

export interface PolicyDecision {
  decision?: string;
  /** the tick that made it */
  as_of?: string;
  reason?: string;
  deployment?: string;
  runtime?: string;
  job_id?: string;
  task_id?: string;
}

export interface DecisionsEnvelope {
  decisions?: PolicyDecision[];
  paused?: boolean;
}

export function fetchDecisions(): Promise<DecisionsEnvelope> {
  return proxyPolicy<DecisionsEnvelope>("decisions");
}

// ---- Compute fleet ------------------------------------------------------

/** `GET /admin/proxy/compute/units` marshals compute.ComputeUnit, which
 * has no json tags (internal/compute/types.go:52-75) -- so the wire keys
 * are the Go field names, capitals and all.
 *
 * Only the fields the cockpit renders are declared, and that is not
 * laziness: the same struct also carries `BootstrapPlain`,
 * `BootstrapTokenHash` and `WorkerTokenHash`, which are worker
 * credentials. JsonTable's generic renderer would print every key it sees,
 * secrets included; naming the safe fields here is what keeps a
 * bootstrap token off an admin's screen (and out of a screenshot). */
/** How Compute reports a failure: a structured error, not a sentence
 * (09-errors-and-retries.md's {source, code, message}, the same shape every
 * service in the stack uses). A unit that carries one is usually a FAILED
 * one, so this is not a rare branch -- it is the branch an operator opens
 * this page for. */
export interface ComputeUnitError {
  source?: string;
  code?: string;
  message?: string;
}

export interface ComputeUnitRaw {
  ID?: string;
  Deployment?: string;
  Runtime?: string;
  Backend?: string;
  Offering?: string;
  Hardware?: string;
  Pricing?: unknown;
  State?: string;
  Error?: ComputeUnitError;
  CreatedAt?: string;
  ReadyAt?: string;
  StoppedAt?: string;
  FailedAt?: string;
  LastHeartbeatAt?: string;
  IdleSince?: string;
}

export function fetchComputeUnits(): Promise<ComputeUnitRaw[]> {
  return proxyCompute<ComputeUnitRaw[]>("units");
}

// ---- Rule E's judgements (ADR-0064) ------------------------------------

/** One judgement of Policy's live estimate, as Compute's
 * GET /admin/anomalies lists it (08-contracts.md §3): a worker stage far
 * off its peers, and what Policy did about it -- ANOMALY (reported only),
 * KEEP (slower, but the result still pays for it) or STOP (ended at once,
 * the Job re-placed). Costs are minor units. */
export interface Anomaly {
  id: number;
  compute_unit_id: string;
  deployment: string;
  runtime: string;
  stage: string;
  verdict: "ANOMALY" | "KEEP" | "STOP";
  eta_s?: number;
  cost_keep_minor?: number;
  cost_replace_minor?: number;
  value_minor?: number;
  reason: string;
  at: string;
}

export function fetchAnomalies(): Promise<Anomaly[]> {
  return proxyCompute<{ anomalies: Anomaly[] }>("anomalies").then((r) => r.anomalies ?? []);
}

// ---- task counts --------------------------------------------------------

/** Queued and running Task counts, from Core's GET /admin/summary (one
 * GROUP BY, exact at any size). They are task counts, not job counts. */
export interface TaskCounts {
  queued: number;
  running: number;
}

export function taskCounts(summary: AdminSummary): TaskCounts {
  return { queued: summary.tasks["QUEUED"] ?? 0, running: summary.tasks["RUNNING"] ?? 0 };
}

/** Newest tasks, for the overview's activity tail. */
export function fetchRecentTasks(limit = 25): Promise<TaskView[]> {
  return adminListTasks({ limit });
}
